import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface User {
  id: string;
  username: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  bio?: string;
  tokens: number;
  streak: number;
  level: number;
  isVip: boolean;
}

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  login: (email: string, password: string) => Promise<void>;
  signup: (username: string, email: string, password: string) => Promise<void>;
  logout: () => void;
  checkAuth: () => Promise<void>;
  updateTokens: (tokens: number) => void;
  refreshUser: () => Promise<void>;
  updateProfile: (updates: {
    displayName?: string;
    username?: string;
    avatarUrl?: string;
  }) => Promise<void>;
}

const API_URL = import.meta.env.VITE_API_URL || 'https://api.streakhq.app';

async function parseResponse(response: Response) {
  const text = await response.text();
  try {
    return JSON.parse(text);
  } catch {
    return { message: text || 'An error occurred' };
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) => set({ user, isAuthenticated: !!user }),
      setToken: (token) => set({ token }),

      login: async (email, password) => {
        const response = await fetch(`${API_URL}/auth/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const data = await parseResponse(response);

        if (!response.ok) {
          throw new Error(data.error?.message || data.message || 'Login failed');
        }

        set({
          user: data.user,
          token: data.token,
          isAuthenticated: true,
        });
      },

      signup: async (username, email, password) => {
        const response = await fetch(`${API_URL}/auth/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username, email, password }),
        });

        const data = await parseResponse(response);

        if (!response.ok) {
          throw new Error(data.error?.message || data.message || 'Signup failed');
        }

        set({
          user: data.user,
          token: data.token,
          isAuthenticated: true,
        });
      },

      logout: () => {
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        });
      },

      checkAuth: async () => {
        const { token } = get();

        if (!token) {
          set({ isLoading: false });
          return;
        }

        try {
          const response = await fetch(`${API_URL}/api/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const user = await parseResponse(response);
            set({ user, isAuthenticated: true, isLoading: false });
          } else {
            set({ user: null, token: null, isAuthenticated: false, isLoading: false });
          }
        } catch {
          set({ isLoading: false });
        }
      },

      updateTokens: (tokens) => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, tokens } });
        }
      },

      refreshUser: async () => {
        const { token } = get();
        if (!token) return;

        try {
          const response = await fetch(`${API_URL}/api/me`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const user = await parseResponse(response);
            set({ user, isAuthenticated: true });
          }
        } catch {
          // Silent fail on refresh
        }
      },

      updateProfile: async (updates) => {
        const { token, refreshUser } = get();
        if (!token) throw new Error('Not authenticated');

        const response = await fetch(`${API_URL}/api/me`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(updates),
        });

        const data = await parseResponse(response);

        if (!response.ok) {
          throw new Error(data.error?.message || data.message || 'Update failed');
        }

        // Update user in state with returned user data
        if (data.user) {
          set({ user: data.user });
        } else {
          // If no user in response, refresh from API to ensure sync
          await refreshUser();
        }
      },
    }),
    {
      name: 'streak-auth',
      partialize: (state) => ({ token: state.token }),
    }
  )
);
